import React, { useEffect, useState, } from 'react';
import '../admin.list.scss';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { ColumnsType, SorterResult, } from 'antd/lib/table/interface';
import { IPreviewAgentprofile, PermissionEnum, CrudEnum, TAgentprofileSortableListCol, } from 'atlas-shared';
import { Table, } from 'antd';
import { AdminListPageHeader, ListPageEmpty, } from '@Components';
import { IWithListProps, IWithListSorterProps, withList, } from '@Hocs/with.list';
import { useAbilities, useAuth, useAgentprofiles, useOrganizations, usePauses, useQueues, useTeams, } from '@Hooks';
import { AgentprofileListColumns, } from '@Utils';
import { IAuth, } from '@Store';

const AgentprofileList = ({ filters, filtersCount, hasFilters, sorter, pagination, setQueryParams, resetQueryParams, }: IWithListProps<IPreviewAgentprofile, TAgentprofileSortableListCol>) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth: IAuth = useAuth();
  const agentprofiles = useAgentprofiles();
  const abilities = useAbilities();
  const [loading, setLoading, ] = useState<boolean>(true);
  const [rows, setRows, ] = useState<Array<any>>([]);
  const organizations = useOrganizations();
  const pauses = usePauses();
  const queues = useQueues();
  const teams = useTeams();
  
  const [showDeleted, setShowDeleted, ] = useState(false);
  const [deletedCount, setDeletedCount, ] = useState<number>(0);
  
  useEffect(() => {
    if (!agentprofiles.loaded)
      return;
      
    setLoading(false);
    setRows((!showDeleted ? agentprofiles.agentprofiles.filter(p => !p.deleted_at) : agentprofiles.agentprofiles || []).map(row => ({ ...row, key: row.id, })));
    setDeletedCount(agentprofiles.agentprofiles.filter(agentprofile => !!agentprofile.deleted_at).length);
  }, [agentprofiles, showDeleted, ]);

  const columns: ColumnsType<IPreviewAgentprofile> = React.useMemo(() => {
    return AgentprofileListColumns(t, navigate, showDeleted, organizations, pauses, queues, teams, abilities, auth, [], filters, sorter);
  }, [t, navigate, showDeleted, organizations, pauses, queues, teams, abilities, auth, filters, sorter, ]);

  return <div className='admin-list-view'>
    <AdminListPageHeader 
      t={t}
      title={t('AGENTPROFILES')}
      filtersCount={filtersCount}
      reset={() => resetQueryParams()}
      deleted={[showDeleted, setShowDeleted, ]} deletedCount={deletedCount}
      addBtn={abilities.canImplicit(null, CrudEnum.Insert, PermissionEnum.Agentprofile) ? { callback: () => navigate('/admin/agentprofile/add'), } : undefined} 
    />
    <div className='table-wrp'>
      <Table
        key={loading ? 1 : 0}
        columns={columns}
        dataSource={rows}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
        }}
        onChange={(pagination, filters, _sorter) => {
          const sorter: Array<SorterResult<any>> = Array.isArray(_sorter) ? _sorter : _sorter?.columnKey ? [_sorter, ] : [];

          setQueryParams({
            pagination: { current: pagination.current as number, pageSize: pagination.pageSize as number, },
            filters: Object.fromEntries(Object.entries(filters).filter(([key, value, ]) => value !== null)),
            sorter: sorter
              .filter(s => !!s.order)
              .map(s => ({ column: s.columnKey as TAgentprofileSortableListCol, order: s.order as IWithListSorterProps<IPreviewAgentprofile, TAgentprofileSortableListCol>['order'], })),
          });
        }}
        locale={{
          emptyText: <ListPageEmpty t={t} hasFilters={hasFilters} hasRows={!!rows.length} reset={() => resetQueryParams()} />,
        }}
      />
    </div>
  </div>;
};

export const AgentprofileListView = React.memo(withList<IPreviewAgentprofile, TAgentprofileSortableListCol>(AgentprofileList, {
  filters: {},
  sorter: [{ column: 'created_at', order: 'descend', }, ],
  pagination: { current: 1, pageSize: 10, },
}));
